import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useState, useEffect, useMemo, useCallback } from "react";
import { AddressAutofill } from "@mapbox/search-js-react";
import styled from "styled-components";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { InputDS, LaunchPage, Spacer, TextCapitalized } from "@qivia/ui";
import { createGlobalStyle } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDb,
  selectListAllVehiclesInDbStatus,
} from "../vehicles/vehiclesSlice";
import { selectCompany } from "../homeSlice";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../../../i18n/i18n";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import { LocationOnFilled } from "@qivia/ui";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { fakeVehicleGeolocatedForDemo, getDistanceInKm } from "./lib";
import {
  getLastDataPointsByCompanyAndDateAsync,
  selectLastDataPointsVehicles,
  selectLastDataPointsVehiclesStatus,
  selectDataPointsByVehicleStatus,
} from "./geolocationSlice";
import { GeolocationSidePanel } from "./sidePanel";
import { MaterialUiSingleDatePicker } from "@qivia/ui";
import { DataPoint } from "./geolocationApi";
import { VehiclesShowOnMap } from "./vehiclesShowOnMap";
import { VehicleTripOnMap } from "./vehiclesTripOnMap";

const accessToken = import.meta.env["VITE_MAPBOX_TOKEN"] as string;

export type VehicleGeolocated = {
  uuid: string;
  registrationNumber: string;
  model?: string;
  reference: string;
  lastLatitude: number;
  lastLongitude: number;
  distance: number;
  driverName: string | null;
  driverPhone: string | null;
  dataPoints: DataPoint[];
};

const RecenterMap = ({ center }: { center: [number, number] }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, 14);
  }, [center, map]);
  return null;
};

export const Geolocation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const vehiclesList = useAppSelector(selectListAllVehiclesInDb);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const lastDataPointsVehiclesStatus = useAppSelector(
    selectLastDataPointsVehiclesStatus,
  );
  const dataPointsByVehicleStatus = useAppSelector(
    selectDataPointsByVehicleStatus,
  );
  const lastDataPointsVehicles = useAppSelector(selectLastDataPointsVehicles);
  const [date, setDate] = useState<Date>(new Date());
  const [center, setCenter] = useState<[number, number]>([48.8566, 2.3522]); // Paris coordinates
  const [recenterKey, setRecenterKey] = useState(0);
  const [addressCoordinates, setAddressCoordinates] = useState<
    [number, number] | null
  >(null);
  const [addressDisplayed, setAddressDisplayed] = useState("");

  const [vehiclesToDisplay, setVehiclesToDisplay] = useState<
    VehicleGeolocated[]
  >([]);
  const [hoveredVehicleId, setHoveredVehicleId] = useState<string | null>(null);
  const [showTripForVehicleUuid, setShowTripForVehicleUuid] = useState<
    string | null
  >(null);

  const isDemoEnvironment = useCallback(() => {
    return import.meta.env["VITE_TARGET_ENV"] === "demo";
  }, []);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(
        listAllVehiclesInDbAsync({ companyUuid: company?.uuid || "" }),
      );
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && lastDataPointsVehiclesStatus === "idle") {
      void dispatch(
        getLastDataPointsByCompanyAndDateAsync({
          companyUuid: company.uuid,
          date: date.toISOString(),
        }),
      );
    }
  }, [company, date, dispatch, lastDataPointsVehiclesStatus]);

  const lastDataPointsVehiclesToDisplayed = useMemo<VehicleGeolocated[]>(() => {
    if (!lastDataPointsVehicles) return [];
    return lastDataPointsVehicles.reduce((acc, vehicle) => {
      const vehicleData = vehiclesList.find(
        (v) => v.uuid === vehicle.vehicleUuid,
      );
      if (!vehicleData) return acc;
      acc.push({
        uuid: vehicleData.uuid,
        registrationNumber: vehicleData.registrationNumber,
        model: vehicleData.model,
        reference: vehicleData.reference,
        lastLatitude: vehicle.lat,
        lastLongitude: vehicle.lon,
        dataPoints: vehicle.dataPoints,
        distance: 0,
        driverName: vehicleData.driverName,
        driverPhone: vehicleData.driverPhone,
      });
      return acc;
    }, [] as VehicleGeolocated[]);
  }, [lastDataPointsVehicles, vehiclesList]);

  useEffect(() => {
    const vehiclesEligibleForGeolocationDisplayed = isDemoEnvironment()
      ? [...fakeVehicleGeolocatedForDemo, ...lastDataPointsVehiclesToDisplayed]
      : lastDataPointsVehiclesToDisplayed;

    const vehiclesToDisplay: VehicleGeolocated[] =
      vehiclesEligibleForGeolocationDisplayed.map(
        (vehicle: VehicleGeolocated) => {
          return {
            ...vehicle,
            distance: addressCoordinates
              ? getDistanceInKm(addressCoordinates, [
                  vehicle.lastLatitude,
                  vehicle.lastLongitude,
                ])
              : 0,
          };
        },
      );
    const vehiclesSortedByDistanceInKm = vehiclesToDisplay.sort(
      (a, b) => a.distance - b.distance,
    );
    setVehiclesToDisplay(vehiclesSortedByDistanceInKm);
  }, [
    addressCoordinates,
    isDemoEnvironment,
    lastDataPointsVehiclesToDisplayed,
  ]);

  const handleAutofill = useCallback(
    (coordinates: [number, number]) => {
      const [lon, lat] = coordinates;
      setCenter([lat, lon]);
      setAddressCoordinates([lat, lon]);
    },
    [setCenter, setAddressCoordinates],
  );

  const handleSelectVehicle = useCallback(
    (vehicle: VehicleGeolocated) => {
      setCenter([vehicle.lastLatitude, vehicle.lastLongitude]);
      setRecenterKey((prev) => prev + 1);
    },
    [setCenter, setRecenterKey],
  );

  const loadLastDataPointsByCompanyAndDate = useCallback(
    (date: Date) => {
      if (!company) return;
      setDate(date);
      void dispatch(
        getLastDataPointsByCompanyAndDateAsync({
          companyUuid: company.uuid,
          date: date.toISOString(),
        }),
      );
    },
    [dispatch, company],
  );

  if (company && !company.hasGeolocation && !isDemoEnvironment()) {
    navigate("/home");
  }

  //IF WE WANT TO DISPLAY A BETTER ROUTE
  // const [route, setRoute] = useState<[number, number][]>([]);

  // useEffect(() => {
  //   const fetchRoute = async () => {
  //     const coordsString = vehiclesToShowOnMap
  //       .map((v) => [v.lastLatitude, v.lastLongitude].join(","))
  //       .join(";");
  //     const url = `https://router.project-osrm.org/route/v1/driving/${coordsString}?overview=full&geometries=geojson`;
  //     const res = await fetch(url);
  //     const data = (await res.json()) as {
  //       routes: { geometry: { coordinates: [number, number][] } }[];
  //     };
  //     setRoute(
  //       data.routes[0].geometry.coordinates.map(([lon, lat]) => [lat, lon]),
  //     );
  //   };
  //   void fetchRoute();
  // }, [vehiclesToShowOnMap]);

  // console.log(route);

  if (
    lastDataPointsVehiclesStatus === "processing" ||
    dataPointsByVehicleStatus === "processing"
  ) {
    return <LaunchPage hasBorderRadius />;
  }

  if (!company) {
    return;
  }

  return (
    <StyledFrame>
      <StyledSearchBar>
        {/* @ts-expect-error AddressAutofill is not typed correctly */}
        <AddressAutofill
          accessToken={accessToken}
          onRetrieve={(e) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const address = feature.properties?.place_name;
            if (address) {
              setAddressDisplayed(address);
            }
            const coordinates = feature.geometry?.coordinates;
            if (coordinates) {
              handleAutofill(coordinates as [number, number]);
            }
          }}
        >
          <InputDS
            name="address"
            placeholder={t("geolocation.input.addressPlaceholder")}
            type="SEARCH"
            width={15}
            value={addressDisplayed}
            onChange={(e) => setAddressDisplayed(e.target.value)}
          />
        </AddressAutofill>
        <Spacer x={5} />
        <StyledColumn>
          <Spacer y={0.25} />
          <MaterialUiSingleDatePicker
            onChange={loadLastDataPointsByCompanyAndDate}
            value={date}
          />
        </StyledColumn>
      </StyledSearchBar>
      <GeolocationSidePanel
        vehiclesToDisplay={vehiclesToDisplay}
        setHoveredVehicleId={setHoveredVehicleId}
        handleSelectVehicle={handleSelectVehicle}
      />
      <LeafletStyleOverrides />
      <MapContainer
        center={center}
        zoom={6}
        zoomControl={false}
        style={{ height: "100%", width: "100%" }}
        minZoom={2}
        maxZoom={18}
      >
        <TileLayer
          url={`https://api.mapbox.com/styles/v1/mapbox/light-v10/tiles/{z}/{x}/{y}?access_token=${accessToken}`}
          attribution=""
          tileSize={512}
          zoomOffset={-1}
        />
        {addressCoordinates && (
          <Marker position={addressCoordinates} icon={locationOnIcon}>
            <Tooltip
              direction="top"
              offset={L.point(0, -35)}
              opacity={1}
              permanent={false}
              className="address-tooltip"
            >
              <StyledAddressTooltip>
                <Spacer y={0.75} />
                <StyledFlex>
                  <Spacer x={0.75} />
                  <TextCapitalized>
                    {t("geolocation.tooltip.selectedAddress")}
                  </TextCapitalized>
                  <Spacer x={0.75} />
                </StyledFlex>
                <Spacer y={0.75} />
              </StyledAddressTooltip>
            </Tooltip>
          </Marker>
        )}

        <VehiclesShowOnMap
          handleSelectVehicle={handleSelectVehicle}
          hoveredVehicleId={hoveredVehicleId}
          setShowTripForVehicleUuid={setShowTripForVehicleUuid}
          vehiclesToDisplay={vehiclesToDisplay}
          date={date}
        />

        <VehicleTripOnMap
          showTripForVehicleUuid={showTripForVehicleUuid}
          isDemoEnvironment={isDemoEnvironment}
          fakeVehicleGeolocatedForDemo={fakeVehicleGeolocatedForDemo}
          lastDataPointsVehiclesToDisplayed={lastDataPointsVehiclesToDisplayed}
        />

        <RecenterMap key={recenterKey} center={center} />
      </MapContainer>
      <LeafletStyleOverrides />
    </StyledFrame>
  );
};

const StyledLocationOnIcon = styled.div`
  svg {
    fill: ${colors["colors/accent/900"]};
    height: 2.5rem;
    width: 2.5rem;
  }
`;

const locationOnIcon = L.divIcon({
  className: "custom-location-on-icon",
  html: ReactDOMServer.renderToString(
    <StyledLocationOnIcon>
      <LocationOnFilled />
    </StyledLocationOnIcon>,
  ),
  iconAnchor: [20, 35],
});

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  height: 2rem;
`;
const StyledFrame = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  background-color: ${colors["colors/surfaces/background/background_level0"]};
  border-radius: 0.75rem 0 0 0.75rem;
  overflow: hidden;
`;

const StyledSearchBar = styled.div`
  display: flex;
  position: absolute;
  top: 1.5rem;
  left: 1rem;
  border: none;
  background: none;
  z-index: 1000;
`;

const LeafletStyleOverrides = createGlobalStyle`
  .vehicle-tooltip {
    background-color: transparent;
    border: 0px;
    box-shadow: none;
}

  .address-tooltip {
    background-color: ${colors["colors/surfaces/background/background_level2,5"]};
    border: 0px;
    box-shadow: none;
}
`;

const StyledAddressTooltip = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/black"]};
  border: 1px solid ${colors["colors/surfaces/background/background_level2,5"]};
  border-radius: 0.5rem;
  box-shadow: 0px 4px 16px 0px rgba(51, 51, 51, 0.15);
`;

const StyledFlex = styled.div`
  display: flex;
`;
